/**
 * @fileoverview Shared message parser class for event feed message parsing.
 */

export interface MessageParserParams {
  choice?: string;
  params?: string[];
  description?: string;
  getMessage?: (value: string) => string;
  getAssets?: (value: string) => unknown[];
}

export interface ParsedMessage {
  message: string;
  assets: unknown[];
}

/**
 * Escapes user-provided text for safe interpolation into a regular expression.
 * @param value The raw string value to escape.
 * @returns The escaped string that can be used in a RegExp pattern.
 */
function escapeRegExp(value: string): string {
  return value.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

/**
 * Parses slash commands and other tokenized message fragments.
 */
export default class MessageParser {
  /**
   * The command or token this parser recognizes at the start of a message.
   */
  choice = '';

  /**
   * The ordered parameter names used to limit serialized message arguments.
   */
  params: string[] = [];

  /**
   * Human-readable help text describing what this parser does.
   */
  description = '';

  /**
   * Creates a parser instance with the provided overrides.
   * @param params Optional parser configuration and method overrides.
   */
  constructor(params: MessageParserParams = {}) {
    Object.assign(this, params);
  }

  /**
   * Checks whether this parser's choice begins with the provided prefix.
   * @param value The prefix to compare against the parser choice.
   * @returns True when the parser choice starts with the provided value.
   */
  startsWith(value: string): boolean {
    return new RegExp(`^${escapeRegExp(value)}`, 'i').test(this.choice);
  }

  /**
   * Checks whether the provided message begins with this parser's choice.
   * @param value The message text to inspect.
   * @returns True when the message starts with the parser choice.
   */
  isAtStartOf(value: string): boolean {
    return new RegExp(`^${escapeRegExp(this.choice)}`, 'i').test(value);
  }

  /**
   * Extracts positional arguments that follow the parser choice.
   * @param value The raw message string to serialize.
   * @returns The parsed argument list, limited by the configured params length.
   */
  serialize(value: string): string[] {
    return value
      .trim()
      .split(' ')
      .slice(1, this.params.length + 1);
  }

  /**
   * Removes the parser choice from the message and trims the remainder.
   * @param value The raw message string.
   * @returns The remaining message text after the parser choice is stripped.
   */
  getMessage(value: string): string {
    return value.replace(this.choice, '').trim();
  }

  /**
   * Returns any assets extracted from the current message.
   * @param _value The message text to inspect for assets.
   * @returns An array of extracted assets.
   */
  getAssets(_value: string): unknown[] {
    return [];
  }

  /**
   * Applies a series of parsers to a message and accumulates their assets.
   * @param message The initial message text to parse.
   * @param parsers The ordered parser instances to apply.
   * @returns The final parsed message text and collected assets.
   */
  static parse(message: string, parsers: MessageParser[]): ParsedMessage {
    return parsers.reduce<ParsedMessage>(
      (accumulator, parser) => ({
        message: parser.getMessage(accumulator.message),
        assets: accumulator.assets.concat(parser.getAssets(accumulator.message)),
      }),
      { message, assets: [] },
    );
  }
}
